import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'

import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import {
  renderTemplate,
  findUnknownTags,
  BALANCE_REMINDER_TAGS,
  DEFAULT_BALANCE_REMINDER_TEMPLATES,
} from '@limo/shared'
import { resolvePaymentMode } from '@limo/shared/pricing'

/**
 * Balance reminder settings.
 *
 * The message bodies are rendered with the SAME helper the edge function sends
 * with (`renderTemplate`), so the preview below each field is what the customer
 * actually receives — not an approximation of it.
 */

const SETTINGS = {
  ENABLED: 'balance_reminder_enabled',
  DAYS_BEFORE: 'balance_reminder_days_before',
  SEND_HOUR: 'balance_reminder_send_hour',
  REPEAT_DAILY: 'balance_reminder_repeat_daily',
  CUSTOMER_SMS: 'balance_reminder_customer_sms',
  CUSTOMER_EMAIL: 'balance_reminder_customer_email',
  OPERATOR_SMS: 'balance_reminder_operator_sms',
  OPERATOR_EMAIL: 'balance_reminder_operator_email',
  CUSTOMER_SMS_BODY: 'balance_reminder_customer_sms_body',
  CUSTOMER_EMAIL_SUBJECT: 'balance_reminder_customer_email_subject',
  CUSTOMER_EMAIL_BODY: 'balance_reminder_customer_email_body',
  OPERATOR_SMS_BODY: 'balance_reminder_operator_sms_body',
  OPERATOR_EMAIL_SUBJECT: 'balance_reminder_operator_email_subject',
  OPERATOR_EMAIL_BODY: 'balance_reminder_operator_email_body',
  PAYMENT_MODE: 'booking_payment_mode',
  REQUIRE_DEPOSIT: 'booking_require_deposit',
} as const

const schema = z.object({
  enabled: z.boolean().default(false),
  days_before: z.coerce.number().int().min(1, 'At least 1 day').max(30, 'At most 30 days'),
  send_hour: z.coerce.number().int().min(0).max(23, 'Must be an hour from 0 to 23'),
  repeat_daily: z.boolean().default(false),
  customer_sms: z.boolean().default(true),
  customer_email: z.boolean().default(true),
  operator_sms: z.boolean().default(true),
  operator_email: z.boolean().default(true),
  customer_sms_body: z.string().default(''),
  customer_email_subject: z.string().default(''),
  customer_email_body: z.string().default(''),
  operator_sms_body: z.string().default(''),
  operator_email_subject: z.string().default(''),
  operator_email_body: z.string().default(''),
}).refine(
  // Reminders with every channel off would run the job, write a log row, and
  // send nothing — worse than being switched off, because it looks like it works.
  (data) =>
    !data.enabled ||
    data.customer_sms || data.customer_email || data.operator_sms || data.operator_email,
  {
    message: 'Turn on at least one channel, or switch reminders off',
    path: ['customer_sms'],
  }
)

type FormData = z.infer<typeof schema>

/** Sample values for the previews — realistic, so line lengths look real. */
const PREVIEW_VALUES = {
  customer_name: 'Dana Whitfield',
  first_name: 'Dana',
  company_name: 'Your Company',
  confirmation_code: 'BR-4821',
  pickup_datetime: 'Fri, Aug 22, 2026, 4:30 PM',
  pickup_date: 'Aug 22, 2026',
  pickup_time: '4:30 PM',
  pickup_address: '1200 Market St, Philadelphia, PA',
  dropoff_address: 'PHL International Airport',
  vehicle_name: 'Executive Sedan',
  total_amount: '$540.00',
  amount_paid: '$135.00',
  balance_due: '$405.00',
  payment_link: 'https://yourcompany.com/pay/bp_1a2b3c',
}

function TemplatePreview({ value, html }: { value: string; html?: boolean }) {
  const unknown = findUnknownTags(value)
  const rendered = renderTemplate(value, PREVIEW_VALUES, html ? { html: true } : {})

  return (
    <div className="space-y-1.5">
      <div className="rounded-md bg-muted/50 p-3 text-xs">
        <p className="text-muted-foreground mb-1">Preview</p>
        {html ? (
          <div
            className="prose prose-xs max-w-none [&_p]:my-1"
            // The operator authored this markup themselves and only sees their
            // own account's copy; interpolated VALUES are escaped by the renderer.
            dangerouslySetInnerHTML={{ __html: rendered }}
          />
        ) : (
          <p className="whitespace-pre-wrap">{rendered}</p>
        )}
      </div>
      {unknown.length > 0 && (
        <p className="text-xs text-amber-700 flex items-start gap-1.5">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          {unknown.map((t) => `{{${t}}}`).join(', ')} {unknown.length === 1 ? 'is' : 'are'} not a
          known placeholder and will be left blank.
        </p>
      )}
    </div>
  )
}

export function BalanceRemindersPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}
      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)
      if (error) throw error

      const map: Record<string, string> = {}
      data?.forEach((s) => {
        map[s.key] = s.value || ''
      })
      return map
    },
    enabled: !!activeAccount?.id,
  })

  const d = DEFAULT_BALANCE_REMINDER_TEMPLATES

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      enabled: false,
      days_before: 3,
      send_hour: 8,
      repeat_daily: false,
      customer_sms: true,
      customer_email: true,
      operator_sms: true,
      operator_email: true,
      customer_sms_body: d.customerSms,
      customer_email_subject: d.customerEmailSubject,
      customer_email_body: d.customerEmailBody,
      operator_sms_body: d.operatorSms,
      operator_email_subject: d.operatorEmailSubject,
      operator_email_body: d.operatorEmailBody,
    },
  })

  const enabled = form.watch('enabled')

  useEffect(() => {
    if (!settings) return
    form.reset({
      enabled: settings[SETTINGS.ENABLED] === 'true',
      days_before: Number(settings[SETTINGS.DAYS_BEFORE]) || 3,
      send_hour: settings[SETTINGS.SEND_HOUR] ? Number(settings[SETTINGS.SEND_HOUR]) : 8,
      repeat_daily: settings[SETTINGS.REPEAT_DAILY] === 'true',
      customer_sms: settings[SETTINGS.CUSTOMER_SMS] !== 'false',
      customer_email: settings[SETTINGS.CUSTOMER_EMAIL] !== 'false',
      operator_sms: settings[SETTINGS.OPERATOR_SMS] !== 'false',
      operator_email: settings[SETTINGS.OPERATOR_EMAIL] !== 'false',
      customer_sms_body: settings[SETTINGS.CUSTOMER_SMS_BODY] || d.customerSms,
      customer_email_subject: settings[SETTINGS.CUSTOMER_EMAIL_SUBJECT] || d.customerEmailSubject,
      customer_email_body: settings[SETTINGS.CUSTOMER_EMAIL_BODY] || d.customerEmailBody,
      operator_sms_body: settings[SETTINGS.OPERATOR_SMS_BODY] || d.operatorSms,
      operator_email_subject: settings[SETTINGS.OPERATOR_EMAIL_SUBJECT] || d.operatorEmailSubject,
      operator_email_body: settings[SETTINGS.OPERATOR_EMAIL_BODY] || d.operatorEmailBody,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings])

  // An account that always charges in full never creates a balance, so there is
  // nothing for this feature to chase. Said plainly rather than hiding the page.
  const accountPaymentMode = settings
    ? resolvePaymentMode({
        accountSettings: {
          paymentMode: settings[SETTINGS.PAYMENT_MODE],
          requireDeposit: settings[SETTINGS.REQUIRE_DEPOSIT],
        },
      })
    : 'full'
  const chargesInFull = accountPaymentMode === 'full'

  const ghlConfigured = settings?.['go_high_level_enabled'] === 'true'

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!activeAccount?.id) throw new Error('No account selected')

      const rows = [
        [SETTINGS.ENABLED, String(data.enabled), 'Send balance reminders before pickup'],
        [SETTINGS.DAYS_BEFORE, String(data.days_before), 'Days before pickup to remind'],
        [SETTINGS.SEND_HOUR, String(data.send_hour), 'Local hour to send reminders'],
        [SETTINGS.REPEAT_DAILY, String(data.repeat_daily), 'Repeat daily until paid'],
        [SETTINGS.CUSTOMER_SMS, String(data.customer_sms), 'Text the customer'],
        [SETTINGS.CUSTOMER_EMAIL, String(data.customer_email), 'Email the customer'],
        [SETTINGS.OPERATOR_SMS, String(data.operator_sms), 'Text the operator'],
        [SETTINGS.OPERATOR_EMAIL, String(data.operator_email), 'Email the operator'],
        [SETTINGS.CUSTOMER_SMS_BODY, data.customer_sms_body, 'Customer SMS template'],
        [SETTINGS.CUSTOMER_EMAIL_SUBJECT, data.customer_email_subject, 'Customer email subject'],
        [SETTINGS.CUSTOMER_EMAIL_BODY, data.customer_email_body, 'Customer email template'],
        [SETTINGS.OPERATOR_SMS_BODY, data.operator_sms_body, 'Operator SMS template'],
        [SETTINGS.OPERATOR_EMAIL_SUBJECT, data.operator_email_subject, 'Operator email subject'],
        [SETTINGS.OPERATOR_EMAIL_BODY, data.operator_email_body, 'Operator email template'],
      ].map(([key, value, description]) => ({
        account_id: activeAccount.id,
        key,
        value,
        description,
        is_secret: false,
      }))

      const { error } = await supabase
        .from('account_settings')
        .upsert(rows, { onConflict: 'account_id,key' })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({ title: 'Saved', description: 'Balance reminder settings have been updated.' })
    },
    onError: (error) => {
      toast({
        title: 'Error saving settings',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Balance Reminders</CardTitle>
            <CardDescription>
              Remind customers — and yourself — about money still owed before a pickup.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {chargesInFull && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Your account is set to <strong>Always pay in full</strong>, so bookings never
                leave a balance and nothing will be sent. Change this under Settings &gt;
                Booking to start taking deposits.
              </div>
            )}

            {!ghlConfigured && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Reminders are sent through GoHighLevel, which isn&apos;t connected yet. Set it up
                under Settings &gt; Integrations first — until then reminders will be skipped.
              </div>
            )}

            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Send Balance Reminders</FormLabel>
                    <FormDescription>
                      Run every morning and contact anyone with money outstanding.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={chargesInFull}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {enabled && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="days_before"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Days Before Pickup</FormLabel>
                        <FormControl>
                          <Input type="number" min={1} max={30} className="w-24" {...field} />
                        </FormControl>
                        <FormDescription>
                          Remind about bookings picking up within this many days.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="send_hour"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Send At (your local time)</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-2">
                            <Input type="number" min={0} max={23} className="w-20" {...field} />
                            <span className="text-muted-foreground text-sm">:00</span>
                          </div>
                        </FormControl>
                        <FormDescription>
                          8 means 8am in your timezone, not the server&apos;s.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="repeat_daily"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Repeat Daily Until Paid</FormLabel>
                        <FormDescription>
                          Off by default — each booking is reminded once. Turning this on
                          messages the customer every morning until they pay, which collects
                          more but risks complaints and costs a text each time.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="space-y-3">
                  <p className="text-sm font-medium">Who gets reminded</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {([
                      ['customer_sms', 'Text the customer'],
                      ['customer_email', 'Email the customer'],
                      ['operator_sms', 'Text me'],
                      ['operator_email', 'Email me'],
                    ] as const).map(([name, label]) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                            <FormLabel className="font-normal">{label}</FormLabel>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                  {form.formState.errors.customer_sms?.message && (
                    <p className="text-sm font-medium text-destructive">
                      {form.formState.errors.customer_sms.message}
                    </p>
                  )}
                </div>

                <div className="rounded-lg border p-4 space-y-2">
                  <p className="text-sm font-medium">Placeholders you can use</p>
                  <div className="flex flex-wrap gap-1.5">
                    {BALANCE_REMINDER_TAGS.map(({ tag, description }) => (
                      <code
                        key={tag}
                        title={description}
                        className="text-xs bg-muted px-1.5 py-0.5 rounded cursor-help"
                      >
                        {`{{${tag}}}`}
                      </code>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-medium">Message to the customer</p>

                  <FormField
                    control={form.control}
                    name="customer_sms_body"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Text message</FormLabel>
                        <FormControl>
                          <Textarea rows={3} {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customer_email_subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email subject</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customer_email_body"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email body</FormLabel>
                        <FormControl>
                          <Textarea rows={8} className="font-mono text-xs" {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} html />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-medium">Message to you</p>

                  <FormField
                    control={form.control}
                    name="operator_sms_body"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Text message</FormLabel>
                        <FormControl>
                          <Textarea rows={2} {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="operator_email_subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email subject</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="operator_email_body"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email body</FormLabel>
                        <FormControl>
                          <Textarea rows={8} className="font-mono text-xs" {...field} />
                        </FormControl>
                        <TemplatePreview value={field.value} html />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  )
}
